import { useMemo, useEffect, useState } from 'react';
import { Globe as Globe2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { extractCountryFromEvent, CountryInfo } from '../../utils/countryExtractor';
import { supabase } from '../../lib/supabase';

interface CountryFilterProps {
  events: CalendarEvent[];
  calendarId?: string;
  selectedCountries: string[];
  onSelectionChange: (countries: string[]) => void;
}

interface CountryWithCount extends CountryInfo {
  count: number;
}

export default function CountryFilter({ events, calendarId, selectedCountries, onSelectionChange }: CountryFilterProps) {
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>([]);

  useEffect(() => {
    if (events.length > 0 || !calendarId) return;

    supabase
      .from('calendar_events')
      .select('id, title, description, location, start_datetime, end_datetime, source')
      .eq('calendar_id', calendarId)
      .in('sync_status', ['synced', 'pending_google_sync'])
      .then(({ data }) => {
        if (data && data.length > 0) {
          setLocalEvents(data.map(e => ({
            id: e.id,
            title: e.title,
            start: new Date(e.start_datetime),
            end: new Date(e.end_datetime),
            description: e.description || undefined,
            location: e.location || undefined,
            source: e.source || undefined,
          })));
        }
      });
  }, [events.length, calendarId]);

  const allEvents = events.length > 0 ? events : localEvents;

  const countries = useMemo(() => {
    const countryMap = new Map<string, CountryWithCount>();

    allEvents.forEach(event => {
      const country = extractCountryFromEvent(event);
      if (!country) return;

      const existing = countryMap.get(country.code);
      if (existing) {
        existing.count += 1;
      } else {
        countryMap.set(country.code, { ...country, count: 1 });
      }
    });

    return Array.from(countryMap.values()).sort((a, b) => b.count - a.count);
  }, [allEvents]);

  const [expanded, setExpanded] = useState(false);
  const VISIBLE_LIMIT = 5;

  if (countries.length === 0) return null;

  const hasMore = countries.length > VISIBLE_LIMIT;
  const visibleCountries = expanded ? countries : countries.slice(0, VISIBLE_LIMIT);
  const hiddenCount = countries.length - VISIBLE_LIMIT;

  const toggleCountry = (code: string) => {
    if (selectedCountries.includes(code)) {
      onSelectionChange(selectedCountries.filter(c => c !== code));
    } else {
      onSelectionChange([...selectedCountries, code]);
    }
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe2 className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">Countries</h4>
        </div>
        {selectedCountries.length > 0 && (
          <button
            onClick={clearAll}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>
      <div className="space-y-1">
        {visibleCountries.map(country => {
          const isSelected = selectedCountries.includes(country.code);
          return (
            <button
              key={country.code}
              onClick={() => toggleCountry(country.code)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                isSelected
                  ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-base flex-shrink-0">{country.flag}</span>
                <span className="truncate font-medium">{country.name}</span>
              </div>
              <span className={`text-xs flex-shrink-0 ml-2 px-1.5 py-0.5 rounded-full ${
                isSelected
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {country.count}
              </span>
            </button>
          );
        })}
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Show all ({hiddenCount} more)
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
